import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BuildingIcon, UserIcon } from '../../assets/icons';
import Layout from '../../components/Layout';
import ErrorMessage from '../../components/ErrorMessage';
import AddRecipientWizardShell, { WizardFormFooter } from '../../components/AddRecipientWizard';
import { UserType } from '../../enums/index';
import { getRecipientRequirements } from '../../store/sagas/api';

function BulletItem({ children }) {
  return (
    <li className='flex items-center gap-2 text-[12.5px] text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>
      <span className='h-1 w-1 shrink-0 rounded-full bg-[var(--n300)]' aria-hidden='true' />
      {children}
    </li>
  );
}

BulletItem.propTypes = {
  children: PropTypes.node.isRequired,
};

function RecipientTypeCard({ icon, title, description, bullets, onClick, disabled, enabled }) {
  return (
    <button
      type='button'
      onClick={onClick}
      disabled={disabled}
      className={`flex h-full flex-col gap-3.5 rounded-[14px] border border-[var(--color-border)] bg-[var(--white)] p-7 text-left shadow-[var(--shadow-sm)] transition-[border-color,box-shadow,transform] duration-150 ${
        disabled
          ? 'cursor-not-allowed opacity-50'
          : 'cursor-pointer hover:-translate-y-px hover:border-[var(--n400)] hover:shadow-[var(--shadow-md)]'
      }`}
    >
      <div className='flex h-11 w-11 items-center justify-center rounded-xl border border-[#e5e7eb] bg-[#f5f6f8] text-[var(--n800)]'>
        {icon}
      </div>
      <div>
        <div className='font-display text-[22px] font-normal leading-tight tracking-[-0.015em] text-[var(--n900)]'>{title}</div>
        <p className='mt-1 text-[13px] leading-snug text-[var(--text-secondary)] font-[family-name:var(--font-ui)]'>{description}</p>
      </div>
      <ul className='m-0 flex list-none flex-col gap-1 p-0'>{bullets}</ul>
      {enabled && (
        <div className='mt-auto inline-flex items-center gap-1.5 pt-2 text-[13px] font-medium text-[var(--n800)] font-[family-name:var(--font-ui)]'>
          Continue
          <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
            <path d='M5 12h14M12 5l7 7-7 7' />
          </svg>
        </div>
      )}
    </button>
  );
}

RecipientTypeCard.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  bullets: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  enabled: PropTypes.bool,
};

RecipientTypeCard.defaultProps = {
  disabled: false,
  enabled: false,
};

function SelectRecipientType() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currency = searchParams.get('currency');

  const { user } = useSelector((state) => state.user);
  const { currencies: configCurrencies } = useSelector((state) => state.config);

  const [supportInfo, setSupportInfo] = useState({
    individual: false,
    company: false,
  });
  const [checkingRails, setCheckingRails] = useState(false);
  const [loadingRequirements, setLoadingRequirements] = useState(false);
  const [requirementsError, setRequirementsError] = useState(null);
  const [recipientRequirementsData, setRecipientRequirementsData] = useState(null);
  const fetchedCurrencyRef = useRef(null);

  const getCountryFromCurrency = (currencyCode) => {
    if (!currencyCode || !configCurrencies || configCurrencies.length === 0) return null;

    const normalizedCurrency = currencyCode.toUpperCase();
    const matchedCurrency = configCurrencies.find((c) => c.code === normalizedCurrency);
    if (!matchedCurrency) return null;

    if (matchedCurrency.country && matchedCurrency.country.code) {
      return matchedCurrency.country.code;
    }

    if (matchedCurrency.countries && Array.isArray(matchedCurrency.countries) && matchedCurrency.countries.length > 0) {
      return matchedCurrency.countries[0].code;
    }

    return null;
  };

  useEffect(() => {
    if (!currency) return;

    if (fetchedCurrencyRef.current !== currency) {
      setRecipientRequirementsData(null);
      fetchedCurrencyRef.current = null;
    }

    if (recipientRequirementsData && fetchedCurrencyRef.current === currency) {
      if (recipientRequirementsData && recipientRequirementsData.rails && Array.isArray(recipientRequirementsData.rails)) {
        let supportsIndividual = false;
        let supportsCompany = false;

        recipientRequirementsData.rails.forEach((rail) => {
          if (rail.types && Array.isArray(rail.types)) {
            if (rail.types.includes('individual')) supportsIndividual = true;
            if (rail.types.includes('company')) supportsCompany = true;
          }
        });

        setSupportInfo({
          individual: supportsIndividual,
          company: supportsCompany,
        });
      }
      return;
    }

    const fetchSupportInfo = async () => {
      setLoadingRequirements(true);
      setRequirementsError(null);

      try {
        const bankCountryCode = getCountryFromCurrency(currency);
        const data = await getRecipientRequirements(currency, bankCountryCode);
        setRecipientRequirementsData(data);
        fetchedCurrencyRef.current = currency;

        if (data && data.rails && Array.isArray(data.rails)) {
          let supportsIndividual = false;
          let supportsCompany = false;

          data.rails.forEach((rail) => {
            if (rail.types && Array.isArray(rail.types)) {
              if (rail.types.includes('individual')) supportsIndividual = true;
              if (rail.types.includes('company')) supportsCompany = true;
            }
          });

          setSupportInfo({
            individual: supportsIndividual,
            company: supportsCompany,
          });
        } else {
          setSupportInfo({ individual: false, company: false });
        }
      } catch (error) {
        setSupportInfo({ individual: false, company: false });
        setRecipientRequirementsData(null);
        fetchedCurrencyRef.current = currency;
        setRequirementsError(error);
      } finally {
        setLoadingRequirements(false);
      }
    };

    fetchSupportInfo();
  }, [currency, configCurrencies]);

  const extractRails = (data, recipientTypeLower) => {
    if (!data || !data.rails || !Array.isArray(data.rails)) return [];

    const rails = [];
    const seenRails = new Set();
    data.rails.forEach((rail) => {
      if (rail.name && rail.types && Array.isArray(rail.types) && rail.types.includes(recipientTypeLower)) {
        if (!seenRails.has(rail.name)) {
          rails.push(rail.name);
          seenRails.add(rail.name);
        }
      }
    });
    return rails;
  };

  const handleRecipientTypeSelect = async (type) => {
    if (!currency || requirementsError) return;

    setCheckingRails(true);

    try {
      const recipientTypeLower = type === 'individual' ? 'individual' : 'company';
      let data = recipientRequirementsData;
      if (!data) {
        const bankCountryCode = getCountryFromCurrency(currency);
        data = await getRecipientRequirements(currency, bankCountryCode);
      }

      const uniqueRails = extractRails(data, recipientTypeLower);

      if (uniqueRails.length > 1) {
        navigate(`/select-rail?currency=${currency}&recipientType=${type}`, { replace: true });
      } else if (uniqueRails.length === 1) {
        navigate(`/add-recipient-form?currency=${currency}&recipientType=${type}&rail=${uniqueRails[0]}`, { replace: true });
      } else {
        setRequirementsError('No transfer methods are available for this currency on your account.');
      }
    } catch (error) {
      setRequirementsError(error);
    } finally {
      setCheckingRails(false);
    }
  };

  const userType = user?.userType;

  const individualDescription =
    userType === UserType.Individual ? 'Send to yourself or another person' : 'Send to a person';
  const companyDescription =
    userType === UserType.Individual ? 'Send to a Company' : 'Send to your Company or another Business';

  const handleBack = () => {
    navigate('/select-currency');
  };

  const handleCancel = () => {
    navigate('/recipients');
  };

  const cardsDisabled = Boolean(requirementsError) || loadingRequirements;

  return (
    <Layout
      hideHeader
      mainPaddingClassName='px-6 py-9 sm:px-11'
      floatBack={{ onClick: handleBack, label: 'Back', ariaLabel: 'Back to currency selection' }}
    >
      <AddRecipientWizardShell
        currentStep={2}
        title='What kind of recipient?'
        subtitle="Choose whether you're paying a business or an individual — we'll ask for the right details next."
        footer={<WizardFormFooter onCancel={handleCancel} showContinue={false} />}
      >
        {requirementsError && (
          <div className='mb-4'>
            <ErrorMessage error={requirementsError} title='Currency not available' />
          </div>
        )}

        {(checkingRails || loadingRequirements) && (
          <div className='py-8 text-center'>
            <p className='text-sm text-[var(--text-secondary)] font-[family-name:var(--font-ui)]'>Loading transfer methods...</p>
          </div>
        )}

        {!checkingRails && !loadingRequirements && (
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
            <RecipientTypeCard
              icon={<UserIcon className='h-5 w-5' />}
              title='Individual'
              description={individualDescription}
              bullets={
                <>
                  <BulletItem>Full legal name</BulletItem>
                  <BulletItem>Personal IBAN</BulletItem>
                  <BulletItem>Address (for some routes)</BulletItem>
                </>
              }
              onClick={() => handleRecipientTypeSelect('individual')}
              disabled={cardsDisabled || !supportInfo.individual}
              enabled={!cardsDisabled && supportInfo.individual}
            />
            <RecipientTypeCard
              icon={<BuildingIcon className='h-5 w-5' />}
              title='Business / Company'
              description={companyDescription}
              bullets={
                <>
                  <BulletItem>Company name</BulletItem>
                  <BulletItem>Registration &amp; VAT</BulletItem>
                  <BulletItem>Business IBAN</BulletItem>
                </>
              }
              onClick={() => handleRecipientTypeSelect('company')}
              disabled={cardsDisabled || !supportInfo.company}
              enabled={!cardsDisabled && supportInfo.company}
            />
          </div>
        )}
      </AddRecipientWizardShell>
    </Layout>
  );
}

export default SelectRecipientType;
